import { label } from '../../../lib/labels'
import type { DonutSlice } from '../../insights/charts/DonutChart'
import type { WaterfallStep } from '../../insights/charts/WaterfallChart'
import type { ComputationRunView } from '../../../types/calc'
import type { QualifiedSpendLineView } from '../../../types/film'
import type { TimelineEntry } from '../../../types/timeline'
import type { AdjustmentHistoryRowView } from './AdjustmentsHistoryTable'
import type { FilingStatusStep } from './FilingStatusTimeline'

/**
 * Pure, unit-testable composition helpers for the `/cases/{id}/analytics` tab (IB-282 View 2) —
 * each source degrades independently and is asserted to never fabricate a value; kept separate from
 * `useCaseAnalytics`'s async orchestration so every mapping rule has a fast, dependency-free test
 * (CLAUDE.md §5's "give any pure/deterministic component a fast unit test" rule), mirroring
 * `screens/insights/portfolioPipeline.ts`'s own split.
 */

function formatAmount(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(amount)
  } catch {
    return `${currency ?? ''} ${amount.toFixed(2)}`.trim()
  }
}

// ---------------------------------------------------------------------------
// 2.1 — QRE / qualified-spend breakdown (donut, ≤6 + other)
// ---------------------------------------------------------------------------

const MAX_DONUT_SLICES = 6

/**
 * Groups a bounded, already-loaded set of `QualifiedSpendLine`s (film only — see RAID R-UX282-1)
 * into a ≤6-slices-plus-"other" donut, largest first (design-system.md §5.1's composition-chart
 * convention). Returns `null` for an empty input — the caller renders the tile's own documented
 * empty state ("No qualified spend recorded yet"), never a $0 donut (DoR AC2.1).
 */
export function buildSpendBreakdown(
  rows: QualifiedSpendLineView[],
): { slices: DonutSlice[]; totalDisplay: string } | null {
  if (rows.length === 0) return null

  const byCategory = new Map<string, number>()
  for (const row of rows) {
    byCategory.set(row.category, (byCategory.get(row.category) ?? 0) + row.amount)
  }
  const currency = rows[0]?.currency ?? 'USD'
  const total = rows.reduce((sum, r) => sum + r.amount, 0)

  const sorted = Array.from(byCategory.entries()).sort((a, b) => b[1] - a[1])
  const head = sorted.slice(0, MAX_DONUT_SLICES)
  const tail = sorted.slice(MAX_DONUT_SLICES)

  const slices: DonutSlice[] = head.map(([category, amount]) => ({
    key: category,
    label: label('spendCategory', category),
    value: amount,
  }))
  if (tail.length > 0) {
    slices.push({ key: '__other', label: 'Other', value: tail.reduce((sum, [, amount]) => sum + amount, 0) })
  }

  return { slices, totalDisplay: formatAmount(total, currency) }
}

// ---------------------------------------------------------------------------
// 2.2 — Credit computation breakdown (waterfall over the pinned FINAL run's own provenance)
// ---------------------------------------------------------------------------

/**
 * Builds `WaterfallStep[]` from the pinned FINAL `ComputationRun`'s own default-scenario
 * `provenance` (falling back to `computedOutputs` if a scenario carries no per-node provenance —
 * the same two-tier degrade `CreditPanel.tsx#ComputationBreakdown` already uses). Node order is
 * preserved exactly as the server returned it (DAG evaluation order) — never re-sorted client-side
 * (design doc §6's determinism note).
 *
 * <b>Deliberately does not fabricate increase/decrease semantics.</b> The DAG's `provenance` map
 * carries each node's own independent computed value, not a delta against a running total — there is
 * no real "kind" (base/uplift/cap) tag on the wire today (a config-driven CalculationGraph names its
 * own node keys; this reads them verbatim, never a hardcoded jurisdiction-specific key). Every step
 * therefore renders as an absolute checkpoint bar (`kind: 'total'`, "=" prefix) rather than a stacked
 * delta — real DAG-node values, honestly presented, not an invented waterfall narrative the data
 * doesn't support. The first node is tagged `'base'` and the last `'total'` purely for the visual
 * bookend cue (WaterfallChart treats all three checkpoint kinds identically); the ones in between are
 * `'subtotal'`.
 */
export function buildComputationWaterfall(run: ComputationRunView | null): WaterfallStep[] {
  if (!run) return []
  const scenario = run.scenarios[0]
  if (!scenario) return []

  const provenanceEntries = Object.entries(scenario.provenance)
  const entries: [string, number][] =
    provenanceEntries.length > 0
      ? provenanceEntries
          .map(([key, entry]): [string, number | null] => [key, toFiniteNumber(entry.value)])
          .filter((e): e is [string, number] => e[1] !== null)
      : Object.entries(scenario.computedOutputs)
          .map(([key, value]): [string, number | null] => [key, toFiniteNumber(value)])
          .filter((e): e is [string, number] => e[1] !== null)

  return entries.map(([key, value], index) => ({
    key,
    label: key,
    value: Math.abs(value),
    kind: index === 0 ? 'base' : index === entries.length - 1 ? 'total' : 'subtotal',
  }))
}

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(n) ? n : null
}

// ---------------------------------------------------------------------------
// 2.3 — Evidence readiness gap list
// ---------------------------------------------------------------------------

export interface ReadinessGapView {
  id: string
  label: string
}

/**
 * Maps `ReadinessScoreView.gaps` (a `Record<string, unknown>[]` — either a `{reason}` empty-universe
 * marker or a `{targetType, targetId}` unlinked-subject entry, `ReadinessScoreService.computeProxy`'s
 * own two shapes) onto the gap-list props `CaseAnalyticsPanel` renders. Never fabricates a label for
 * a shape it doesn't recognize — an unrecognized gap still renders (as its raw JSON), never dropped.
 */
export function buildReadinessGaps(gaps: Record<string, unknown>[]): ReadinessGapView[] {
  return gaps.map((gap, index) => {
    if (typeof gap.reason === 'string') {
      const known: Record<string, string> = {
        no_evidenceable_subjects: 'No evidenceable subjects on this filing yet.',
        no_linked_evidence: 'No evidence linked to any subject yet.',
      }
      return { id: `reason-${index}`, label: known[gap.reason] ?? gap.reason }
    }
    if (typeof gap.targetType === 'string' && typeof gap.targetId === 'string') {
      return {
        id: gap.targetId,
        label: `${gap.targetType.replace(/_/g, ' ')} ${gap.targetId.slice(0, 8)} — no evidence linked`,
      }
    }
    return { id: `gap-${index}`, label: JSON.stringify(gap) }
  })
}

// ---------------------------------------------------------------------------
// 2.4 — Filing-status timeline (real, hash-chained events, ADR-0046)
// ---------------------------------------------------------------------------

/** The 5 real `ProgramFiling` milestone event types this filing's own Timeline can carry
 * (`case_.api.CaseEventTypes`) — `case.closed.v1` is deliberately excluded: it is a `Case`-aggregate
 * event, never returned by a `target=ProgramFiling,{id}` query. Fixed, canonical display order —
 * never re-derived from arrival order (design doc §6). */
const MILESTONE_STEPS: { eventType: string; key: string; stepLabel: string }[] = [
  { eventType: 'program-filing.materialized.v1', key: 'materialized', stepLabel: 'Materialized' },
  { eventType: 'program-filing.certified.v1', key: 'certified', stepLabel: 'Certified' },
  { eventType: 'program-filing.amended.v1', key: 'amended', stepLabel: 'Amended' },
  { eventType: 'program-filing.outcome-recorded.v1', key: 'outcome_recorded', stepLabel: 'Outcome recorded' },
  { eventType: 'program-filing.archived.v1', key: 'archived', stepLabel: 'Archived' },
]

/**
 * Maps the real `GET /api/v1/events?target=ProgramFiling,{id}` feed (newest-first,
 * `TimelinePanel`'s own documented ordering) onto the fixed milestone stepper. A milestone with a
 * matching event is `'complete'`; the LAST milestone (in canonical order) with a matching event is
 * `'current'` (never a later one, even if amend fired after certify — amend does not "undo"
 * certify); everything after it is `'pending'`. Any event whose `eventType` is not one of the 5
 * milestones folds into `additionalEventCount`, never silently dropped (design doc §2.4/§3).
 */
export function buildFilingStatusTimeline(
  events: TimelineEntry[],
): { steps: FilingStatusStep[]; additionalEventCount: number } {
  const milestoneEventTypes = new Set(MILESTONE_STEPS.map((m) => m.eventType))
  const additionalEventCount = events.filter((e) => !milestoneEventTypes.has(e.eventType)).length

  // `events` is newest-first; for each milestone type, the FIRST matching entry encountered is the
  // most recent occurrence of that milestone.
  const latestByType = new Map<string, TimelineEntry>()
  for (const event of events) {
    if (milestoneEventTypes.has(event.eventType) && !latestByType.has(event.eventType)) {
      latestByType.set(event.eventType, event)
    }
  }

  let lastCompleteIndex = -1
  MILESTONE_STEPS.forEach((m, index) => {
    if (latestByType.has(m.eventType)) lastCompleteIndex = index
  })

  const steps: FilingStatusStep[] = MILESTONE_STEPS.map((m, index) => {
    const event = latestByType.get(m.eventType) ?? null
    const state: FilingStatusStep['state'] =
      index < lastCompleteIndex ? 'complete' : index === lastCompleteIndex ? 'current' : 'pending'
    return {
      key: m.key,
      label: m.stepLabel,
      state,
      occurredAt: event?.occurredAt ?? null,
      actorLabel: event ? (event.actorId ? `Actor ${event.actorId.slice(0, 8)}` : 'System') : null,
      channel: event?.channel ?? null,
    }
  })

  return { steps, additionalEventCount }
}

// ---------------------------------------------------------------------------
// 2.5 — Adjustments history (every guarded-field change touching this case)
// ---------------------------------------------------------------------------

interface RawAdjustmentSource {
  targetType: string
  targetId: string
  targetLabel?: string
  fieldName: string
  priorValue: number | null
  newValue: number
  currencyCode?: string | null
  method: string
  reason: string
  actorId: string | null
  channel: string | null
  appliedAt: string
}

function formatFieldValue(value: number | null, currencyCode: string | null | undefined): string {
  if (value === null) return '—'
  if (currencyCode) return formatAmount(value, currencyCode)
  return `${value}%`
}

/**
 * Merges every source of guarded-field adjustment already loaded for this case (QSL rows — real
 * today; ProgramFiling/CreditInstrument rows — the small additive endpoints this story adds) into
 * one target-agnostic, newest-first table (DoR AC2.5). Each source degrades independently — a source
 * that 404s/errors upstream is simply absent from `sources`, never blanking the merged table; never a
 * fabricated merge across sources with different clocks (this is a stable sort on `appliedAt`, not a
 * heuristic).
 */
export function mergeAdjustmentHistory(sources: RawAdjustmentSource[]): AdjustmentHistoryRowView[] {
  return sources
    .map((s, index) => ({
      id: `${s.targetType}-${s.targetId}-${s.fieldName}-${s.appliedAt}-${index}`,
      targetType: s.targetType,
      targetId: s.targetId,
      targetLabel: s.targetLabel ?? s.targetType,
      fieldName: s.fieldName,
      priorValueDisplay: formatFieldValue(s.priorValue, s.currencyCode),
      newValueDisplay: formatFieldValue(s.newValue, s.currencyCode),
      method: s.method,
      reason: s.reason,
      actorLabel: s.actorId ? `Actor ${s.actorId.slice(0, 8)}` : null,
      channel: s.channel,
      appliedAt: s.appliedAt,
    }))
    .sort((a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime())
}

export type { RawAdjustmentSource }
